// @ts-nocheck
// material
import { Container, Button, Dialog, DialogTitle, DialogContent, DialogActions, Box, Stack, Typography, TextField, InputAdornment, Skeleton, Tooltip } from '@mui/material';
// hooks
import useAuth from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import LoadingButton from '@mui/lab/LoadingButton';
import { useEffect, useState } from 'react';
import { userRoles } from '../../constants/defaultValues';
import useSettings from '../../hooks/useSettings';
import SunEditor from '../../components/editor/suneditor';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import './TableStyle.css';
import { useSnackbar } from 'notistack';
import { MIconButton } from '../../components/@material-extend';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import axios from '../../utils/axios';

// components
import Page from '../../components/Page';
import PageHeading from '../../components/PageHeading';
import Logo from '../../components/Logo';
import DocumentSheet from '../../components/documents/DocumentSheet';

// ----------------------------------------------------------------------

// A clickable profession row. The whole row opens the profession's sections;
// no price is shown or edited here (SWMS pricing was removed).
const RowCard = styled('button')(({ theme }) => ({
  width: '100%',
  textAlign: 'left',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  background: theme.palette.background.paper,
  font: 'inherit',
  color: 'inherit',
  transition: theme.transitions.create(['border-color', 'background-color', 'transform']),
  '&:hover': {
    borderColor: theme.palette.primary.main,
    background: theme.palette.action.hover,
  },
  '&:active': { transform: 'translateY(1px)' },
}));

const listStyle = { marginLeft: 'auto', marginRight: 'auto', maxWidth: 720 };

export default function Swms() {
  const { themeStretch } = useSettings();
  const [content, setContent] = useState('');
  const [documents, setDocuments] = useState([]);
  const [editorModalOpen, toggleEditorModal] = useState(false);
  const [docModalOpen, toggleDocModal] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [currentDoc, setCurrentDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [query, setQuery] = useState('');
  const [createModalOpen, toggleCreateModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [duplicateModalOpen, toggleDuplicateModal] = useState(false);
  const [duplicateSource, setDuplicateSource] = useState(null);
  const [duplicateTitle, setDuplicateTitle] = useState('');
  const [duplicating, setDuplicating] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const toast = (type, message) => {
    enqueueSnackbar(message, {
      variant: type,
      action: (key) => (
        <MIconButton size="small" onClick={() => closeSnackbar(key)}>
          <Icon icon={closeFill} />
        </MIconButton>
      ),
    });
  };

  useEffect(() => {
    if (user && user.id && user.role !== userRoles.admin && user.role !== userRoles.superadmin) {
      navigate('/');
    }
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setFetching(true);
    try {
      const r = await axios.get('/v1/documents/profession-list?type=swms');
      setDocuments(r.data?.data || r.data || []);
    } catch (e) {
      console.error('fetchDocuments', e);
    } finally {
      setFetching(false);
    }
  };

  const fetchSections = async (documentId) => {
    try {
      const r = await axios.get(`/v1/documents/doc-sections?documentId=${documentId}`);
      return r.data?.data || r.data || [];
    } catch (e) {
      console.error('fetchSections', e);
      return [];
    }
  };

  const decode = (raw) => {
    if (!raw) return '';
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'string' ? parsed : raw;
    } catch {
      return raw;
    }
  };

  const handleSaveDocumentSection = async () => {
    if (!content || !content.length || !currentDoc?.id) return;
    setLoading(true);
    try {
      if (currentSection?.id) {
        await axios.put(`/v1/documents/doc-sections/${currentSection.id}`, {
          documentId: currentDoc.id,
          content: JSON.stringify(content),
        });
      } else {
        await axios.post('/v1/documents/doc-sections', {
          documentId: currentDoc.id,
          content: JSON.stringify(content),
        });
      }
      const sections = await fetchSections(currentDoc.id);
      setCurrentDoc((prev) => ({ ...prev, sections }));
      toggleEditorModal(false);
      setContent('');
      setCurrentSection(null);
    } catch (e) {
      console.error('handleSaveDocumentSection', e);
      toast('warning', e.message || 'Failed to save section');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProfession = async () => {
    const title = newTitle.trim();
    if (!title) return;
    setCreating(true);
    try {
      await axios.post('/v1/documents/profession-list', { title, type: 'swms' });
      setNewTitle('');
      toggleCreateModal(false);
      await fetchDocuments();
    } catch (e) {
      console.error('handleCreateProfession', e);
      toast('warning', e.message || 'Failed to create profession');
    } finally {
      setCreating(false);
    }
  };

  const handleOpenDuplicate = (doc) => {
    setDuplicateSource(doc);
    setDuplicateTitle(`${doc.title} (copy)`);
    toggleDuplicateModal(true);
  };

  const handleDuplicateProfession = async () => {
    const title = duplicateTitle.trim();
    if (!title || !duplicateSource?.id) return;
    setDuplicating(true);
    try {
      await axios.post(`/v1/documents/profession-list/${duplicateSource.id}/duplicate`, { title });
      toggleDuplicateModal(false);
      setDuplicateSource(null);
      setDuplicateTitle('');
      await fetchDocuments();
    } catch (e) {
      console.error('handleDuplicateProfession', e);
      toast('warning', e.message || 'Failed to duplicate profession');
    } finally {
      setDuplicating(false);
    }
  };

  const handleNewSection = () => {
    setCurrentSection(null);
    setContent('');
    toggleEditorModal(true);
  };

  // Click a section row to edit it (no hover-only buttons).
  const handleEditSection = (section) => {
    setCurrentSection({ ...section });
    setContent(decode(section.content));
    toggleEditorModal(true);
  };

  const handleDelete = async (section) => {
    if (!currentDoc?.id) return;
    try {
      await axios.delete(`/v1/documents/doc-sections/${section.id}?documentId=${currentDoc.id}`);
      const sections = await fetchSections(currentDoc.id);
      setCurrentDoc((prev) => ({ ...prev, sections }));
    } catch (e) {
      console.error('handleDelete', e);
      toast('warning', e.message || 'Failed to delete section');
    }
  };

  const handleDocClick = async (doc) => {
    const sections = await fetchSections(doc.id);
    setCurrentDoc({ ...doc, sections });
    toggleDocModal(true);
  };

  return (
    <Page title="WeSafe - SWMS Documents">
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <PageHeading
          title="SWMS"
          subtitle="Safe Work Method Statements by profession. Click a profession to edit its sections."
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setNewTitle('');
                toggleCreateModal(true);
              }}
            >
              New profession
            </Button>
          }
        />

        <Box sx={listStyle}>
          <TextField
            fullWidth
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search professions"
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
          {fetching ? (
            Array.from({ length: 8 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={56}
                sx={{ mb: 1, borderRadius: 1.5 }}
              />
            ))
          ) : (() => {
            const q = query.trim().toLowerCase();
            const filtered = q
              ? documents.filter((d) => (d.title || '').toLowerCase().includes(q))
              : documents;
            if (documents.length === 0) {
              return (
                <Typography sx={{ color: 'text.secondary', py: 6, textAlign: 'center' }}>
                  No professions yet.
                </Typography>
              );
            }
            if (filtered.length === 0) {
              return (
                <Typography sx={{ color: 'text.secondary', py: 6, textAlign: 'center' }}>
                  No professions match “{query}”.
                </Typography>
              );
            }
            return filtered.map((doc) => (
              <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <RowCard type="button" onClick={() => handleDocClick(doc)} sx={{ flex: 1, mb: 0 }}>
                  <Typography sx={{ fontWeight: 600 }}>{doc.title}</Typography>
                  <ChevronRightIcon sx={{ color: 'text.disabled' }} />
                </RowCard>
                <Tooltip title="Duplicate profession">
                  <IconButton
                    aria-label="duplicate profession"
                    onClick={() => handleOpenDuplicate(doc)}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ));
          })()}
        </Box>

        {/* Create profession */}
        <Dialog
          fullWidth
          maxWidth="sm"
          open={createModalOpen}
          onClose={() => toggleCreateModal(false)}
        >
          <DialogTitle>New Profession</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Profession name"
              placeholder="e.g. Electrician"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateProfession();
              }}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => toggleCreateModal(false)}>Cancel</Button>
            <LoadingButton
              loading={creating}
              loadingPosition="start"
              onClick={handleCreateProfession}
              startIcon={<AddIcon />}
              variant="contained"
              disabled={!newTitle.trim()}
            >
              Create
            </LoadingButton>
          </DialogActions>
        </Dialog>

        {/* Duplicate profession */}
        <Dialog
          fullWidth
          maxWidth="sm"
          open={duplicateModalOpen}
          onClose={() => toggleDuplicateModal(false)}
        >
          <DialogTitle>Duplicate Profession</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Copies “{duplicateSource?.title}” and its document template into a new profession.
            </Typography>
            <TextField
              fullWidth
              label="New profession name"
              value={duplicateTitle}
              onChange={(e) => setDuplicateTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleDuplicateProfession();
              }}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => toggleDuplicateModal(false)}>Cancel</Button>
            <LoadingButton
              loading={duplicating}
              loadingPosition="start"
              onClick={handleDuplicateProfession}
              startIcon={<ContentCopyIcon />}
              variant="contained"
              disabled={!duplicateTitle.trim()}
            >
              Duplicate
            </LoadingButton>
          </DialogActions>
        </Dialog>

        {/* Section editor */}
        <Dialog fullWidth maxWidth="lg" open={editorModalOpen} onClose={() => toggleEditorModal(false)}>
          <DialogTitle>{currentSection ? 'Update Section' : 'New Section'}</DialogTitle>
          <DialogContent>
            {editorModalOpen && (
              <div className="editor">
                <SunEditor setContents={content} onChange={(c) => setContent(c)} />
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => toggleEditorModal(false)}>Cancel</Button>
            <LoadingButton
              loading={loading}
              loadingPosition="start"
              onClick={handleSaveDocumentSection}
              startIcon={<SaveIcon />}
              variant="contained"
            >
              {currentSection ? 'Update' : 'Save'}
            </LoadingButton>
          </DialogActions>
        </Dialog>

        {/* Profession sections viewer/editor */}
        <Dialog fullWidth maxWidth="lg" open={docModalOpen} onClose={() => toggleDocModal(false)}>
          <DialogTitle>{currentDoc ? currentDoc.title : ''}</DialogTitle>
          <DialogContent>
            {currentDoc && (
              <Stack spacing={2} sx={{ py: 1 }}>
                {(currentDoc.sections || []).map((section) => (
                  <Box key={section.id} sx={{ position: 'relative' }}>
                    <IconButton
                      size="small"
                      aria-label="delete section"
                      onClick={() => handleDelete(section)}
                      sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    <DocumentSheet
                      html={decode(section.content)}
                      onClick={() => handleEditSection(section)}
                      header={<Logo showText sx={{ height: 26 }} />}
                      footer="Copyright © WeSafe Pty Ltd · document generated at WeSafe.com.au"
                    />
                  </Box>
                ))}
                <Button
                  fullWidth
                  startIcon={<AddIcon />}
                  onClick={handleNewSection}
                  sx={{
                    py: 2,
                    border: '1.5px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    color: 'text.secondary',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  Add new section
                </Button>
              </Stack>
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </Page>
  );
}
